import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useWhoopAutoSync } from '@/hooks/useWhoopSync';

interface WhoopSyncContextValue {
  isConnected: boolean;
  isSyncing: boolean;
  lastSync: string | null;
  triggerSync: () => void;
}

const WhoopSyncContext = createContext<WhoopSyncContextValue | null>(null);

// Memoized provider to prevent unnecessary re-renders
export function WhoopSyncProvider({ children }: { children: ReactNode }) {
  const syncState = useWhoopAutoSync();
  
  // Memoize context value to prevent re-renders when values haven't changed
  const contextValue = useMemo(() => ({
    isConnected: syncState.isConnected,
    isSyncing: syncState.isSyncing,
    lastSync: syncState.lastSync,
    triggerSync: syncState.triggerSync,
  }), [syncState.isConnected, syncState.isSyncing, syncState.lastSync, syncState.triggerSync]);
  
  return (
    <WhoopSyncContext.Provider value={contextValue}>
      {children}
    </WhoopSyncContext.Provider>
  );
}

export function useWhoopSyncContext() {
  const context = useContext(WhoopSyncContext);
  if (!context) {
    // Return a stable default value if not wrapped in provider (graceful fallback)
    return {
      isConnected: false,
      isSyncing: false,
      lastSync: null,
      triggerSync: () => {},
    };
  }
  return context;
}

export default WhoopSyncProvider;
